import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import { dayMs } from "verdi-common/timeConstants";
import type { AxisSpec, StreamEntry } from "../ChartSpec";
import type { StreamPoint } from "../stream-cache/stream-cache-store";
import type { WindowedStreams } from "../stream-data/types";

/** Converts a raw database value to the display unit. Receives a unitCategory string (from the wire). */
export type GetConversion = (unitCategory: string) => (value: number) => number;

export type PreparedSeries = {
    streamId: number;
    color: string;
    name: string;
    /** Which sensor cluster on the device — the tiebreaker when two series share a device and a category. */
    sensorClusterName: string | null;
    /** Which channel on that cluster — the tiebreaker when two series share a cluster too. */
    sensorElementName: string | null;
    /** The axis this series is bound to — chart options and hover marks both address it by id. */
    axisId: string;
    /** From the series' axis — legend shows the localized category under the device name. */
    unitCategory: string;
    points: [Date, number | null][];
};

// Bring together the spec and the stream data to produce ECharts series
export function prepareSeries(
    dataByStreamId: WindowedStreams,
    streams: StreamEntry[],
    axes: AxisSpec[],
    getConversion: GetConversion,
): PreparedSeries[] {
    if (!dataByStreamId || Object.keys(dataByStreamId).length === 0 || !streams || streams.length === 0) {
        return [];
    }

    return streams
        .filter((stream) => dataByStreamId[stream.streamId])
        .map((stream) => {
            const data = dataByStreamId[stream.streamId];
            const axis = axes.find((a) => a.id === stream.axisId);

            // Axes are built from these streams, so a matching axis always exists.
            if (!axis) {
                throw new Error(`No axis found for stream ${stream.streamId} (axisId ${stream.axisId})`);
            }

            const convert = getConversion(axis.unitCategory);

            const staleAfterMs = getUnitCategoryConfiguration(axis.unitCategory)?.staleAfterMs ?? dayMs;

            const nullInsertedPoints: [Date, number | null][] = [];

            data.points
                // Empty buckets arrive as nulls; drop them so the time delta below is calculated between real readings
                .filter((point): point is StreamPoint & { avg: number } => point.avg !== null)
                .forEach((point, index, array) => {
                    nullInsertedPoints.push([new Date(point.t), convert(point.avg)]);

                    const nextPoint = array[index + 1];

                    // With ECharts connectNulls: false, inserting our own null value breaks the line when
                    // we've detected a stale period
                    if (nextPoint && nextPoint.t - point.t > staleAfterMs) {
                        nullInsertedPoints.push([new Date(point.t + staleAfterMs), null]);
                    }
                });

            return {
                color: stream.color,
                streamId: stream.streamId,
                name: stream.name,
                sensorClusterName: stream.sensorClusterName ?? null,
                sensorElementName: stream.sensorElementName ?? null,
                axisId: axis.id,
                unitCategory: axis.unitCategory,
                points: nullInsertedPoints,
            };
        });
}
